import React, { useEffect, useState } from "react";
import { Smartphone, Share } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  getDeferredInstallPrompt, clearDeferredInstallPrompt, isAppInstalled,
} from "@/lib/pwa";

/**
 * Small "Instalar app" pill shown at the bottom of the sidebar.
 *
 * Rules:
 *  - Hidden if the app is already running as a PWA (`display-mode: standalone`).
 *  - Chrome / Android → uses the native `beforeinstallprompt` event.
 *  - iOS Safari → shows a small dialog with the manual "Compartir → Añadir
 *    a pantalla de inicio" instructions.
 *  - Always available: clicking clears the "dismissed" flag from the banner,
 *    so the user is never stuck after accidentally closing it.
 */
export default function InstallAppButton() {
  const [available, setAvailable] = useState(false);
  const [installed, setInstalled] = useState(isAppInstalled());
  const [showIosDialog, setShowIosDialog] = useState(false);

  useEffect(() => {
    if (installed) return;
    if (isIOS()) {
      setAvailable(true);
      return;
    }
    if (getDeferredInstallPrompt()) setAvailable(true);
    const onAvail = () => setAvailable(true);
    const onInstalled = () => {
      setAvailable(false);
      setInstalled(true);
    };
    window.addEventListener("pwa-install-available", onAvail);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-install-available", onAvail);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, [installed]);

  if (installed || !available) return null;

  const onClick = async () => {
    // Always clear the "banner dismissed" flag so the invitation banner
    // can also reappear after the user interacted with this button.
    try { localStorage.removeItem("efcm_pwa_install_dismissed_v1"); } catch { /* ignore */ }

    if (isIOS()) {
      setShowIosDialog(true);
      return;
    }
    const deferred = getDeferredInstallPrompt();
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice.catch(() => null);
    clearDeferredInstallPrompt();
    setAvailable(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="install-app-button"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border border-purple-200 bg-purple-50 text-purple-800 hover:bg-purple-100 transition-colors"
      >
        <Smartphone className="h-3.5 w-3.5" />
        Instalar app
      </button>

      <Dialog open={showIosDialog} onOpenChange={setShowIosDialog}>
        <DialogContent className="max-w-sm" data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle>Instalar en tu iPhone / iPad</DialogTitle>
            <DialogDescription>
              iOS no permite instalar apps automáticamente. Sigue estos pasos en Safari:
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-slate-700 mt-2">
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold grid place-items-center shrink-0">1</span>
              <span>
                Pulsa el icono <Share className="inline h-4 w-4 -mt-0.5" /> <b>Compartir</b> en la
                barra inferior de Safari.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold grid place-items-center shrink-0">2</span>
              <span>
                Desplázate hacia abajo y elige <b>"Añadir a pantalla de inicio"</b>.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold grid place-items-center shrink-0">3</span>
              <span>
                Pulsa <b>"Añadir"</b> arriba a la derecha. El icono aparecerá en tu pantalla de
                inicio, como cualquier app.
              </span>
            </li>
          </ol>
          <p className="mt-3 text-xs text-slate-500">
            Si no ves el botón Compartir, abre la web en <b>Safari</b> (no funciona desde Chrome o
            Firefox en iOS por decisión de Apple).
          </p>
        </DialogContent>
      </Dialog>
    </>
  );
}

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}
